import React from 'react';

import {
  Dropdown,
  DropdownOptionType,
  MultiSelectButton,
  TableCell,
  TableHeaderCell,
} from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateEditCallbackArgs,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatCondition, NULL_VALUE } from '@hcs/utils';

const FIELD = PropertyStateFields.condition;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Condition';
const labelShort = 'Condition';
const propertyStatePath: PropertyStatePaths =
  '/propertyValue/valueAtSixConditions/conditionClass';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[PropertyStateFields.condition];
  } else if (propertyStateType === 'Core') {
    return propertyState?.propertyValue?.valueAtSixConditions?.conditionClass;
  }
  return undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatCondition(getValue(propertyStateArgs));

const formatValueShort: FieldConfig['formatValueShort'] = (
  propertyStateArgs,
) => {
  const value = getValue(propertyStateArgs);
  return value ? `C${value}` : NULL_VALUE;
};

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  formatValue(propertyStateArgs);
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};
const FILTER_OPTIONS: DropdownOptionType<number | null>[] = [
  { value: null, label: 'Any' },
  { value: 6, label: 'Excellent' },
  { value: 5, label: 'Nearly New' },
  { value: 4, label: 'Well Maintained' },
  { value: 3, label: 'Worn But Adequate' },
  { value: 2, label: 'Significant Repairs' },
  { value: 1, label: 'Uninhabitable' },
];

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
}: PropertyStateFilterProps<typeof FIELD>) => {
  const value = spatialFiltersInput.conditionClass?.[0] ?? null;

  return (
    <MultiSelectButton
      singleSelect={true}
      dataHcName={`${FIELD}-filter`}
      value={value}
      options={FILTER_OPTIONS}
      disabled={disabled}
      className={className}
      onChange={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            ...spatialFiltersInput,
            conditionClass: value ? [value] : [],
          },
        });
      }}
    />
  );
};
const DEFAULT_ORDER: number[] = [6, 5, 4, 3, 2, 1];

const OPTIONS: DropdownOptionType<number>[] = DEFAULT_ORDER.map(
  (condition) => ({
    value: condition,
    label: formatCondition(condition),
  }),
);

// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateArgsProps & {
  onChange: (args: PropertyStateEditCallbackArgs<typeof FIELD>) => void;
  className?: string;
}) => {
  const value = getValue(propertyStateArgs);

  return (
    <Dropdown
      dataHcName="condition-edit-dropdown"
      ariaLabel={`Adjust ${label}`}
      className={className}
      onSelect={(value) => {
        onChange({
          path: propertyStatePath,
          value,
          field: FIELD,
        });
      }}
      options={OPTIONS}
      value={value}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const CONDITION_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatValueShort,
  formatInline,
  Filter,
  HeaderCell,
  ContentCell,
  Display,
  Edit,
};
